import { SetStateAction, useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { GET_CITY_LINK_INTERFACE } from '../../api/cityReviewLinks';
import Image from 'next/image';
import { useRouter } from 'next/router';

interface IProps {
  data: GET_CITY_LINK_INTERFACE | undefined;
  setShow: React.Dispatch<SetStateAction<boolean>>;
  showFeedBack: boolean;
  setShowFeedBack: React.Dispatch<SetStateAction<boolean>>;
  id: string | undefined;
  customer?: string;
  guide?: string;
  tour?: string;
  tourDate?: string;
  tourTime?: string;
  guidePhone?: string;
  customerPhone?: string;
}

interface MessageState {
  text: string;
  error: boolean;
  errMsg: string;
}

export enum ReviewStatus {
  GOOGLE_REVIEW = 'GOOGLE_REVIEW',
  WEBSITE_FEEDBACK = 'WEBSITE_FEEDBACK',
  PENDING = 'PENDING'
}

const FeedBackModal = ({
  data,
  showFeedBack,
  setShowFeedBack,
  id,
  customer,
  guide,
  tour,
  tourDate,
  tourTime,
  guidePhone,
  customerPhone
}: IProps) => {
  const [message, setMessage] = useState<MessageState>({
    text: '',
    error: false,
    errMsg: ''
  });
  const [thankYou, setThankYou] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const router = useRouter();

  const handleChange = (e: any) => {
    if (e.target.value.length < 4) {
      setMessage({
        error: true,
        text: e.target.value,
        errMsg: 'We appreciate any feedback you might have!'
      });
      return;
    }
    setMessage({ errMsg: '', text: e.target.value, error: false });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (message.text.length < 4) {
      setMessage({
        ...message,
        error: true
      });
    }
    if (message.error) {
      return;
    } else {
      setMessage({
        ...message,
        error: false
      });
      setLoading(true);
      const res = await axios.post(
        `${process.env.NEXT_PUBLIC_HEROKU_SMS_SERVER}/bad-review`,
        {
          feedback: message.text.trim(),
          bookingId: id,
          status: ReviewStatus.WEBSITE_FEEDBACK,
          customer,
          guide,
          tour,
          tourDate,
          tourTime,
          guidePhone,
          customerPhone
        }
      );
      if (res) {
        setLoading(false);
      }
      setLoading(false);
      if (res.status === 200) {
        setThankYou(true);
        setShowFeedBack(false);
        router.push('/reviews');
      }
      if (res.status !== 200)
        toast.error('Something went wrong please try again later');
    }
  };

  return (
    <>
      {showFeedBack && (
        <div
          id="modal-wrapper"
          className="fixed top-0 left-0 h-full w-full z-50 bg-[#21252945] flex justify-center items-center"
          onClick={(e: any) => {
            if (e.target.id === 'modal-wrapper') {
              setShowFeedBack(false);
            }
          }}
        >
          <div className="px-5 py-2 bg-white rounded-md max-w-[85%]">
            {!thankYou && (
              <>
                <div className="text-right">
                  <Image
                    src="https://res.cloudinary.com/see-sight-tours/image/upload/v1685544129/Remaining%20images/Reviews/radix-icons_cross-2_mlrxew.svg"
                    width={25}
                    height={25}
                    alt="close-button"
                    className="ml-auto cursor-pointer"
                    onClick={() => setShowFeedBack(false)}
                  />
                </div>
                <h2 className="text-2xl lg:text-3xl text-center text-[#374045] font-medium">
                  How can we improve our experience?
                </h2>
                <form onSubmit={handleSubmit}>
                  <textarea
                    value={message.text}
                    minLength={4}
                    maxLength={200}
                    placeholder="Feedback"
                    onChange={handleChange}
                    className="border border-slate-300  w-full min-h-[150px] p-1 items-start rounded-sm mt-2"
                  />
                  {message.error && (
                    <div className="text-red-500">{message.errMsg}</div>
                  )}
                  <button className="w-full bg-blue-400 rounded-sm mt-2 text-white text-base text-center p-2">
                    {loading ? (
                      <div role="status" className="flex justify-center">
                        <svg
                          aria-hidden="true"
                          className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>
                      </div>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedBackModal;
